import { defaultLetterSettings, getLetterPoints, getLetterInfo } from '../config/letterSettings.js';

/**
 * Settings Manager for handling game configuration
 */
class SettingsManager {
  constructor() {
    this.currentSettings = defaultLetterSettings;
    this.settingsKey = 'wordrive-letter-settings';
  }

  /**
   * Load settings from localStorage or use defaults
   */
  loadSettings() {
    try {
      const savedSettings = localStorage.getItem(this.settingsKey);
      if (savedSettings) {
        this.currentSettings = JSON.parse(savedSettings);
      }
    } catch (error) {
      console.warn('Failed to load settings from localStorage:', error);
      this.currentSettings = defaultLetterSettings;
    }
    return this.currentSettings;
  }

  /**
   * Save current settings to localStorage
   */
  saveSettings(settings = this.currentSettings) {
    try {
      localStorage.setItem(this.settingsKey, JSON.stringify(settings));
      this.currentSettings = settings;
    } catch (error) {
      console.error('Failed to save settings to localStorage:', error);
    }
  }

  /**
   * Reset settings to defaults
   */
  resetToDefaults() {
    this.currentSettings = { ...defaultLetterSettings };
    this.saveSettings();
    return this.currentSettings;
  }

  /**
   * Get current settings
   */
  getSettings() {
    return this.currentSettings;
  }

  /**
   * Update a specific letter's settings
   */
  updateLetterSettings(letter, newSettings) {
    if (this.currentSettings[letter]) {
      this.currentSettings[letter] = { ...this.currentSettings[letter], ...newSettings };
      this.saveSettings();
    }
  }

  /**
   * Get points for a specific letter
   */
  getLetterPoints(letter) {
    return getLetterPoints(letter, this.currentSettings);
  }

  /**
   * Get full info for a specific letter
   */
  getLetterInfo(letter) {
    return getLetterInfo(letter, this.currentSettings);
  }

  /**
   * Generate a simple points object for backward compatibility
   */
  getPointsObject() {
    const points = {};
    Object.keys(this.currentSettings).forEach(letter => {
      points[letter] = this.currentSettings[letter].points;
    });
    return points;
  }

  /**
   * Generate weighted letter distribution based on frequency and rarity
   */
  generateWeightedLetterPool(totalLetters = 100) {
    const pool = [];
    const settings = this.currentSettings;
    
    Object.keys(settings).forEach(letter => {
      const letterInfo = settings[letter];
      let weight = letterInfo.frequency;
      
      // Reduce weight for rare letters
      if (letterInfo.isRare) {
        weight = Math.max(1, Math.floor(weight * 0.3));
      }
      
      // Add letters to pool based on weight
      for (let i = 0; i < weight && pool.length < totalLetters; i++) {
        pool.push(letter);
      }
    });
    
    return pool;
  }
}

// Create a singleton instance
const settingsManager = new SettingsManager();

export default settingsManager;
